'use client';
import { ICarouselHeroFields } from '@/types/contentful';
import map from 'lodash/map';
import React, { useEffect, useState } from 'react';
import {
  A11y,
  Autoplay,
  EffectFade,
  Navigation,
  Pagination
} from 'swiper/modules';
import 'swiper/css/bundle';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import FeatureTicker from '../FeatureTicker';
import CarouselHeroSlideContent from './CarouselHeroSlideContent';
import { ScrollReveal } from '../ScrollReveal';

const clearBulletAnimation = () => {
  document
    .querySelectorAll<HTMLDivElement>('.custom-bullet-inner')
    .forEach((el) => {
      el.style.width = '0';
      el.style.transition = 'none';
    });
};

const CarouselHero: React.FC<ICarouselHeroFields> = ({
  slides: slidesRaw,
  lazyPreloadPrevNext,
  transitionDelay,
  eagerLoadFirstNImages,
  ticker
}) => {
  const [readyState, setReadyState] = useState(0);

  const ref = React.useRef<SwiperRef>(null);

  useEffect(() => {
    if (ref.current) {
      const swiperEl = ref.current.swiper.el;
      swiperEl.addEventListener('click', () => {
        // Stop swiper auto-transition if any click.  This prevents
        // CTA experience from getting interrupted by auto-transition.
        // This also prevents the user from clicking on a button and
        // having the auto-transition move them to a different slide
        // before they can see the result of their click.
        ref.current?.swiper.autoplay.stop();
      });
    }
  }, [ref.current]);

  const animateBullet = () => {
    requestAnimationFrame(clearBulletAnimation);

    const timeToNextSlide = transitionDelay ?? 1;

    const activeBullet = document.querySelector<HTMLSpanElement>(
      `.carousel-bullet-active .custom-bullet-inner`
    );

    if (!activeBullet) return;

    // Force repaint for transition to work.
    requestAnimationFrame(() => {
      activeBullet.style.width = '100%';
      activeBullet.style.transition = `width ${timeToNextSlide}s linear`;
    });
  };

  useEffect(() => {
    setReadyState(readyState + 1);
  }, []);

  // useMediaQuery is not valid until after initial render. This helps to avoid content jumping.
  useEffect(() => {
    // Small delay to ensure swiper is ready.
    if (readyState === 2) {
      setTimeout(() => animateBullet(), 50);
    }
  }, [readyState]);

  const slides = map(slidesRaw, 'fields');

  let userInitiated = false;

  return (
    <ScrollReveal className="hero-container mb-12 tablet:mb-16" data-e2e="hero">
      <Swiper
        className="desktop:h-[630px] desktop:max-h-[630px] desktop:min-h-[630px]"
        ref={ref}
        modules={[Navigation, Pagination, A11y, Autoplay, EffectFade]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={1000}
        loop={true}
        data-testid="carousel-hero"
        touchEventsTarget="container"
        lazyPreloadPrevNext={lazyPreloadPrevNext}
        autoplay={{
          delay: (transitionDelay ?? 1) * 1000
        }}
        onInit={() => {
          setReadyState(readyState + 1);
        }}
        onTouchStart={() => {
          userInitiated = true;
        }}
        onSlideChange={() => {
          if (userInitiated) {
            const activeBullet = document.querySelector<HTMLSpanElement>(
              '.carousel-bullet-active .custom-bullet-inner'
            );
            if (activeBullet) {
              requestAnimationFrame(clearBulletAnimation);

              requestAnimationFrame(() => {
                activeBullet.style.transition = 'none';
                activeBullet.style.width = '100%';
              });
            }
            return;
          }
          userInitiated = false;
          animateBullet();
          // Animate the active bullet
        }}
        pagination={{
          bulletClass: 'carousel-bullet-container',
          bulletActiveClass: 'carousel-bullet-active',
          horizontalClass: 'carousel-pagination',
          clickable: true,
          type: 'bullets',
          renderBullet: (index: number, className: string) => {
            return `
            <span data-index=${index} class="${className}">
              <span class="carousel-bullet">
                <span class="custom-bullet-inner" data-index="${index}"></span>
              </span>
            </span>
          `;
          }
        }}
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={slide.headline + index}>
            <CarouselHeroSlideContent
              key={index}
              {...slide}
              index={index}
              eagerLoadFirstNImages={eagerLoadFirstNImages}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      {ticker && <FeatureTicker {...ticker?.fields} />}
    </ScrollReveal>
  );
};

export default CarouselHero;
